<template>
  <div :class="classNames">
    <div
      :class="{
        'calendar-loading': loading,
        'mobile-calendar': breakpoints().sm
      }"
    >
      <full-calendar ref="fullCalendar" :options="calendarOptions">
        <template #eventContent="context">
          <timeline-event :context="context" v-bind="$attrs"></timeline-event>
        </template>
        <template #resourceLabelContent="{ resource }">
          <timeline-resource
            :resource="resource"
            v-bind="$attrs"
          ></timeline-resource>
        </template>
      </full-calendar>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { mapState } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";

import AdminTimeClockChannel from "@/channels/AdminTimeClockChannel.js";
import CONSTANTS from "@/Constants.js";
import { breakpoints } from "@/mixins";
import interactionPlugin from "@fullcalendar/interaction";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import FullCalendar from "@fullcalendar/vue3";
import TimelineEvent from "./TimelineEvent.vue";
import TimelineResource from "./TimelineResource.vue";
const { FULLCALENDAR_LICENSE_KEY, SCHEDULE_DEFAULT_ZOOM } = CONSTANTS;

const DEFAULT_SCROLL_TIME = "06:00:00";
const SCHEDULE_MIN_HEIGHT = 320;

export default {
  components: {
    FullCalendar,
    TimelineEvent,
    TimelineResource
  },
  props: {
    options: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: null
    },
    preWorkEnabled: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: SCHEDULE_DEFAULT_ZOOM
    },
    classNames: {
      type: [String, Array, Object],
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      resizeTimeout: null,
      channel: null
    };
  },
  computed: {
    ...mapState({
      timeCardsEnabled({ tenant }) {
        return tenant?.time_cards_enabled;
      },
      isMobileScreenSize() {
        return breakpoints().sm;
      }
    }),
    calendarOptions() {
      return {
        plugins: [
          interactionPlugin,
          resourceTimelinePlugin,
          momentTimezonePlugin
        ],
        themeSystem: "bootstrap4",
        initialView: "resourceTimelineDay",
        schedulerLicenseKey: FULLCALENDAR_LICENSE_KEY,
        editable: true,
        eventStartEditable: true,
        eventResourceEditable: true,
        eventDurationEditable: false,
        headerToolbar: {
          left: "",
          center: "",
          right: ""
        },
        nowIndicator: true,
        scrollTime: DEFAULT_SCROLL_TIME,
        slotMinWidth: this.zoom,
        refetchResourcesOnNavigate: true,
        eventOrder: "-schedule,-travel,start",
        eventMinWidth: 0,
        eventClick: function (info) {
          if (info.event.extendedProps.calendar_event) {
            const calendarEvent = info.event.extendedProps.event;
            EventBus.$emit("edit-calendar-event", calendarEvent);
          }
        },
        dateClick: (info) => {
          if (info.view.type === "resourceTimelineDay") {
            // Only handle double-click for empty slots
            if (info.jsEvent.detail === 2 && info.resource) {
              this.handleEmptySlotDoubleClick(info);
            }
          }
        },
        eventClassNames: this.setEventClassName,
        datesSet: (dateInfo) => {
          // Update calendar height after calls are rendered
          this.api.setOption("height", this.getCalendarHeight());
          // Chain datesSet call
          if (this.options.datesSet) {
            this.options.datesSet(dateInfo);
          }
        },
        resourceAreaWidth: this.isMobileScreenSize ? 100 : 150,
        resourceAreaColumns: [
          {
            headerContent: "Technicians",
            field: "title"
          }
        ],
        resourceGroupField: "department_name",
        resourceOrder: "department_name, installer, title",
        resourceGroupLabelContent: function (e) {
          // display Unassigned as dept name without sorting unassigned to bottom
          return e.groupValue || "Unassigned";
        },
        // FIXME: Auto-collapse based on cache.
        // resourceGroupLabelDidMount: function(info) {
        //   try {
        //     let cachedRows = localStorage.getItem(
        //       "scheduleBoardCollapsedRows"
        //     );
        //     let collapsedRows = cachedRows ? JSON.parse(cachedRows) : [];
        //     if (collapsedRows.includes(info.groupValue)) {
        //       info.el.querySelector(".fc-datagrid-expander").click();
        //     }
        //   } catch (e) {
        //     console.error(e);
        //   }
        // },
        resourceLaneWillUnmount: function (info) {
          if (info.resource.id == 0) {
            // Unassigned was closed
            // add class to disable second sticky row
            $(".fc-resource-timeline").addClass("unassigned-collapsed");
          }
        },
        resourceLaneDidMount: function (info) {
          if (info.resource.id == 0) {
            // Unassigned was opened
            // add class to disable second sticky row
            $(".fc-resource-timeline").removeClass("unassigned-collapsed");
          }
        },
        ...this.options
      };
    },
    api() {
      if (!this.$refs.fullCalendar) {
        return {};
      }
      return this.$refs.fullCalendar.getApi();
    }
  },
  watch: {
    timeCardsEnabled(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.connectToChannel();
      }
    }
  },
  mounted() {
    this.attachDOMListeners();
    // Store calendar instance globally
    // Can use this to quickly access api calls directly
    window._FC = this.api;
    if (this.timeCardsEnabled) {
      this.connectToChannel();
    }
  },
  beforeUnmount() {
    this.removeDOMListeners();
    if (this.channel) this.channel.disconnect();
  },
  methods: {
    async connectToChannel() {
      this.channel = new AdminTimeClockChannel();

      if (this.channel) {
        try {
          await cable.subscribe(this.channel);
          this.channel?.on(
            "tenant-clock-in-status-change",
            this.handleStatusChange
          );
        } catch (err) {
          console.log("App Unable to join Cable server", err);
        }
      }
    },
    handleStatusChange(payload) {
      const { user_id, action } = payload;
      const clockedInIcon = "clocked-in-icon";
      const clockedOutIcon = "clocked-out-icon";
      if (action === "clock_in") {
        $(`.${user_id}-${clockedInIcon}`).removeClass("hidden");
        $(`.${user_id}-${clockedOutIcon}`).addClass("hidden");
      } else {
        $(`.${user_id}-${clockedInIcon}`).addClass("hidden");
        $(`.${user_id}-${clockedOutIcon}`).removeClass("hidden");
      }
    },
    attachDOMListeners() {
      // Adjust schedule height on page resize
      window.addEventListener("resize", this.handleScreenResize);
      window.addEventListener("scroll", this.handleScreenResize);
      // Apply hover class to resource and event rows
      $(document).on(
        "mouseenter",
        "[data-resource-id]",
        this.handleRowMouseEnter
      );
      // Remove hover class to resource and event rows
      $(document).on(
        "mouseleave",
        "[data-resource-id]",
        this.handleRowMouseLeave
      );
      // Collapse resource group on label click
      $(document).on(
        "click",
        ".fc-datagrid-cell.fc-resource-group .fc-datagrid-cell-main",
        this.collapseRow
      );
      // Cache collapsed rows
      // $(document).on("click", ".fc-datagrid-expander", this.cacheCollapsedRow);
    },
    removeDOMListeners() {
      window.removeEventListener("resize", this.handleScreenResize);
      window.removeEventListener("scroll", this.handleScreenResize);
      $(document).off(
        "mouseenter",
        "[data-resource-id]",
        this.handleRowMouseEnter
      );
      $(document).off(
        "mouseleave",
        "[data-resource-id]",
        this.handleRowMouseLeave
      );
      $(document).off(
        "click",
        ".fc-datagrid-cell.fc-resource-group .fc-datagrid-cell-main",
        this.collapseRow
      );
      // $(document).off("click", ".fc-datagrid-expander", this.cacheCollapsedRow);
    },
    collapseRow(e) {
      $(e.target).siblings(".fc-datagrid-expander")[0].click();
    },
    cacheCollapsedRow(e) {
      try {
        const resourceName = $(e.target)
          .siblings(".fc-datagrid-cell-main")
          .text();
        const isClosed = $(e.target).children(".fc-icon-plus-square")[0];
        const cachedRows = localStorage.getItem("scheduleBoardCollapsedRows");
        let collapsedRows = cachedRows ? JSON.parse(cachedRows) : [];
        const isCollapsedCached = collapsedRows.includes(resourceName);
        console.log(isCollapsedCached);
        // FIXME: Fullcalendar bug on initial render making the isClosed check fail
        // need a better way to determine if a row was opened or collapsed
        if (isClosed) {
          collapsedRows.push(resourceName);
        } else if (!isCollapsedCached) {
          collapsedRows = collapsedRows.filter((name) => name !== resourceName);
        }
        localStorage.setItem(
          "scheduleBoardCollapsedRows",
          JSON.stringify(collapsedRows)
        );
      } catch (e) {
        console.error(e);
      }
    },
    handleRowMouseEnter(e) {
      const resource_id = $(e.currentTarget).data("resource-id");
      if (resource_id != 0 && resource_id) {
        return $("[data-resource-id=" + resource_id + "]").addClass(
          "hovered-row"
        );
      }
    },
    handleRowMouseLeave() {
      $(".hovered-row").removeClass("hovered-row");
    },
    handleScreenResize: debounce(function () {
      // wait for header wrap css changes on small screen sizes
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.api.setOption("height", this.getCalendarHeight());
      }, 50);
    }, 150),
    getCalendarHeight() {
      const schedule = $(".orders-calendar")[0];
      if (!schedule) return;
      if (this.isMobileScreenSize) return "auto";
      // Get main page padding minus scroll position
      const PAGE_TOP_PADDING = Math.max(90 - window.scrollY, 30);
      // Get visible height of page header
      const headerHeight = schedule.getBoundingClientRect().top;
      // Subtract top headers from viewport height
      const calculatedScreenSpace =
        window.innerHeight - (headerHeight + PAGE_TOP_PADDING);
      return Math.max(calculatedScreenSpace, SCHEDULE_MIN_HEIGHT);
    },
    handleEmptySlotDoubleClick(info) {
      // Extract user ID from resource (needed for technician selection)
      // Skip if this is the "Unassigned" row (id: 0)
      const technicianUserId =
        info.resource.id === 0
          ? null
          : parseInt(info.resource.extendedProps.user_id) || null;

      // Round down to the start of the hour so clicking anywhere within an hour
      // selects the time slot starting at that hour
      const clickedMoment = moment(info.date).startOf("hour");

      // Check if the scheduled_time for this slot would be in the past
      // (scheduled_time = arrival_window_start + 1 hour, and isPast checks scheduled_time)
      const now = moment();
      const potentialScheduledTime = clickedMoment.clone().add(1, "hour");
      if (potentialScheduledTime.isSameOrBefore(now)) {
        // Cannot schedule an appointment in the past - show validation error
        this.$emit(
          "validation-error",
          "Cannot schedule an appointment in the past."
        );
        return;
      }

      // Add 1 hour to match the backend pattern where scheduled_time = arrival_window_start + 1 hour
      const scheduledTime = clickedMoment
        .clone()
        .add(1, "hour")
        .format("YYYY-MM-DDTHH:mm:ssZ");

      // Emit an event to open the appointment create modal
      this.$emit("open-create-appointment-modal", {
        scheduled_time: scheduledTime,
        technician_id: technicianUserId
      });
    },
    setEventClassName(context) {
      const { event } = context;
      const {
        travel,
        schedule,
        late,
        has_travel,
        needs_attention,
        ghost,
        status,
        assigned,
        pre_work,
        calendar_event
      } = event.extendedProps;
      const classNames = ["elevation-1"];
      if (travel) {
        const isFlat = moment(event.end).diff(event.start, "minutes") < 2;
        if (isFlat) {
          classNames.push("flat-travel");
        }
        classNames.push("travel");
      }
      if (pre_work) {
        classNames.push("prework");
      }
      if (schedule || calendar_event) {
        classNames.push("schedule-event");

        if (calendar_event) {
          if (event.extendedProps.event.exclusive) {
            classNames.push("exclusive");
          } else {
            classNames.push("available");
          }
        }
      }
      if (late) {
        classNames.push("late-event");
      }
      if (has_travel) {
        classNames.push("has-travel");
      }
      if (needs_attention) {
        classNames.push("needs-attention");
      }
      // Adds ghost class
      if (ghost) {
        classNames.push("ghost");
      } else {
        classNames.push("normal");
      }
      if (status) {
        // Adds appointment status classname
        classNames.push(status);
      }
      if (event.id) {
        // Adds appointment id to event element
        // Used for loading edit modal and context menu
        classNames.push(`event-id-${event.id}`);
      }
      if (assigned && status === "pending") {
        classNames.push("assigned");
      }
      return classNames;
    }
  }
};
</script>

<style lang="scss" scoped>
.loader-img {
  height: 180px;
  margin: 20px 0;
}
.loader-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>

<style>
.mobile-calendar {
  min-height: 320px;
  .fc .fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > * {
    top: -25px;
  }
  .fc-resource-timeline-divider {
    top: 0 !important;
  }
}
</style>
